import { useState } from "react";
import { Link } from "react-router-dom";
import type { AdminDeviceRecovery } from "../api/types";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { ErrorNotice, Loading } from "../components/Notice";
import { PageHead } from "../components/PageHead";
import { formatDateTime, lastSeenGap, orDash } from "../format";
import { useAsync, useCompact } from "../hooks";
import { SECTIONS } from "../sections";
import { useSession } from "../session/SessionContext";

/**
 * Members who lost the phone their account is bound to, asking staff to move
 * it to a new one (single-device login, D6).
 *
 * Approving moves the account at once: the member's new phone finishes the
 * move on its next poll, a few seconds later, and the current phone is signed
 * out. That phone was told when the request was made. Denying ends the
 * request. Both are admin only, and never for your own account; the
 * API refuses those with a 409 whose message is shown as-is.
 */
export function DeviceRecoveries() {
  const { api } = useSession();
  const compact = useCompact();
  const queue = useAsync(() => api.deviceRecoveries(), [api]);
  const rows = queue.data?.recoveries ?? [];
  const awaiting = rows.filter((r) => r.status === "awaiting_review").length;

  return (
    <div className="page">
      <PageHead title="Device recovery" icon={<SECTIONS.recoveries.Icon />}>
        <p className="subtle">
          Members moving their account to a new phone without the old one.
          Approve only once you have confirmed it is them: the new phone takes
          over the account straight away.
        </p>
      </PageHead>

      <ErrorNotice error={queue.error} />

      <div className="panel">
        <div className="panel-head">
          <h2>Open requests</h2>
          {queue.data ? <span className="subtle">{awaiting} awaiting review</span> : null}
        </div>
        {queue.loading && !queue.data ? (
          <Loading what="recoveries" />
        ) : rows.length === 0 ? (
          <div className="empty">
            <strong>Nothing to review</strong>
            <p className="subtle">New requests appear here as members make them.</p>
          </div>
        ) : compact ? (
          <ul className="list">
            {rows.map((r) => (
              <RecoveryRow key={r.id} recovery={r} onDecided={queue.reload} compact />
            ))}
          </ul>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Member</th>
                  <th>New phone</th>
                  <th>Current phone</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <RecoveryRow key={r.id} recovery={r} onDecided={queue.reload} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusText({ recovery }: { recovery: AdminDeviceRecovery }) {
  if (recovery.status === "cooling_down") {
    return (
      <span className="chip chip-accent">
        Approved, moving now
      </span>
    );
  }
  return <span className="chip chip-neutral">Awaiting review</span>;
}

const KYC_LABEL = {
  approved: "KYC approved",
  pending: "KYC pending",
  rejected: "KYC rejected",
} as const;

const kycLabel = (r: AdminDeviceRecovery) =>
  r.kycStatus ? KYC_LABEL[r.kycStatus] : "KYC not started";

const platformName = (platform: string | null) =>
  platform === "ios" ? "iOS" : platform === "android" ? "Android" : null;

const phone = (label: string | null, platform: string | null) => {
  if (!label) return "None";
  const os = platformName(platform);
  return os ? `${label} (${os})` : label;
};

function MemberLink({ recovery }: { recovery: AdminDeviceRecovery }) {
  return (
    <Link
      className="cell-link cell-strong"
      to={`/users/${encodeURIComponent(recovery.userId)}`}
      state={{ from: "recoveries" }}
    >
      {recovery.name ?? orDash(recovery.email)}
    </Link>
  );
}

function MemberFacts({ recovery }: { recovery: AdminDeviceRecovery }) {
  return (
    <span className="subtle">
      {recovery.name && recovery.email ? `${recovery.email} · ` : ""}
      {kycLabel(recovery)}
    </span>
  );
}

function MemberCell({ recovery }: { recovery: AdminDeviceRecovery }) {
  return (
    <span className="stack recovery-cell">
      <MemberLink recovery={recovery} />
      <MemberFacts recovery={recovery} />
    </span>
  );
}

function NewPhoneCell({ recovery }: { recovery: AdminDeviceRecovery }) {
  return (
    <span className="stack recovery-cell recovery-phone">
      <span>{recovery.newDeviceLabel} requested</span>
      <span className="subtle">{formatDateTime(recovery.createdAt)}</span>
    </span>
  );
}

function CurrentPhoneCell({ recovery }: { recovery: AdminDeviceRecovery }) {
  if (!recovery.fromDeviceLabel) {
    return (
      <span className="stack recovery-cell">
        <span>No current phone</span>
      </span>
    );
  }
  return (
    <span className="stack recovery-cell recovery-phone">
      <span>{recovery.fromDeviceLabel} current</span>
      {recovery.fromLastSeenAt ? (
        <span className="subtle">last seen {formatDateTime(recovery.fromLastSeenAt)}</span>
      ) : null}
    </span>
  );
}

function RecoveryRow({
  recovery,
  onDecided,
  compact = false,
}: {
  recovery: AdminDeviceRecovery;
  onDecided: () => void;
  compact?: boolean;
}) {
  const [deciding, setDeciding] = useState<"approve" | "deny" | null>(null);
  const awaiting = recovery.status === "awaiting_review";

  const actions = awaiting ? (
    <div className="row recovery-actions">
      <button type="button" className="primary small" onClick={() => setDeciding("approve")}>
        Approve
      </button>
      <button type="button" className="danger small" onClick={() => setDeciding("deny")}>
        Deny
      </button>
    </div>
  ) : null;
  const dialog = deciding ? (
    <DecisionDialog
      recovery={recovery}
      decision={deciding}
      onDone={() => {
        setDeciding(null);
        onDecided();
      }}
      onCancel={() => setDeciding(null)}
    />
  ) : null;

  if (compact) {
    return (
      <li className="list-row list-row-stack">
        <span className="list-main">
          <span className="list-title">
            <span className="list-title-text">
              <MemberLink recovery={recovery} />
            </span>
            <StatusText recovery={recovery} />
          </span>
          <MemberFacts recovery={recovery} />
          <NewPhoneCell recovery={recovery} />
          <CurrentPhoneCell recovery={recovery} />
        </span>
        {actions}
        {dialog}
      </li>
    );
  }

  return (
    <tr>
      <td>
        <MemberCell recovery={recovery} />
      </td>
      <td>
        <NewPhoneCell recovery={recovery} />
      </td>
      <td>
        <CurrentPhoneCell recovery={recovery} />
      </td>
      <td>
        <StatusText recovery={recovery} />
      </td>
      <td>
        {actions}
        {dialog}
      </td>
    </tr>
  );
}

const VERIFY_METHODS = ["intercom", "direct", "video", "other"] as const;
type VerifyMethod = (typeof VERIFY_METHODS)[number];

const VERIFY_LABEL: Record<VerifyMethod, string> = {
  intercom: "Intercom ticket",
  direct: "Contacted the member directly",
  video: "Video call with photo ID",
  other: "Other",
};

const VERIFY_HINT: Record<VerifyMethod, string> = {
  intercom: "Chat from the new phone",
  direct: "Phone or email already on file",
  video: "Face matched their KYC photo",
  other: "Describe what you checked",
};

const VERIFY_PLACEHOLDER: Record<VerifyMethod, string> = {
  intercom: "Ticket link. Date of birth and last card merchant matched.",
  direct: "Called the number on file. Date of birth matched.",
  video: "Google Meet, ID held to camera, matched KYC selfie.",
  other: "What you checked, and how it matched.",
};

function DecisionDialog({
  recovery,
  decision,
  onDone,
  onCancel,
}: {
  recovery: AdminDeviceRecovery;
  decision: "approve" | "deny";
  onDone: () => void;
  onCancel: () => void;
}) {
  const { api } = useSession();
  const [note, setNote] = useState("");
  const [method, setMethod] = useState<VerifyMethod | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const approve = decision === "approve";
  const other = method === "other";
  const detail = note.trim();
  // The stored note leads with the method, so the audit trail reads the same
  // way whichever reviewer wrote it; "Other" is nothing but what they typed.
  const finalNote = approve
    ? method && !other
      ? detail
        ? `${VERIFY_LABEL[method]}: ${detail}`
        : VERIFY_LABEL[method]
      : detail
    : detail;
  const ready = !approve || (method !== null && (!other || detail.length > 0));

  async function submit() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.decideRecovery(recovery.id, decision, finalNote || undefined);
      onDone();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  const member = [recovery.name, recovery.email].filter(Boolean).join(" · ") || recovery.privyDid;

  return (
    <ConfirmDialog
      title={approve ? "Approve recovery" : "Deny recovery"}
      confirmLabel={approve ? "Approve recovery" : "Deny recovery"}
      busyLabel={approve ? "Approving..." : "Denying..."}
      busy={busy}
      // An approval hands the account to a new phone; it carries what was
      // checked, for whoever reads the audit trail later.
      confirmDisabled={!ready}
      danger={!approve}
      onConfirm={() => void submit()}
      onCancel={onCancel}
    >
      <dl className="recovery-facts">
        <div>
          <dt>Member</dt>
          <dd>{member}</dd>
        </div>
        <div>
          <dt>Moves to</dt>
          <dd>
            {phone(recovery.newDeviceLabel, recovery.newPlatform)}, requested{" "}
            {formatDateTime(recovery.createdAt)}
          </dd>
        </div>
        <div>
          <dt>Current phone</dt>
          <dd>
            {recovery.fromDeviceLabel ? (
              <>
                {recovery.fromDeviceLabel}
                {recovery.fromLastSeenAt ? (
                  <>
                    , last seen {formatDateTime(recovery.fromLastSeenAt)}{" "}
                    <span className="chip chip-warn">
                      {lastSeenGap(recovery.fromLastSeenAt, recovery.createdAt)}
                    </span>
                  </>
                ) : null}
              </>
            ) : (
              "None"
            )}
          </dd>
        </div>
        <div>
          <dt>{approve ? "Takes effect" : "Effect"}</dt>
          <dd>
            {approve
              ? "Right after you approve. The current phone is signed out."
              : "The request ends now. The member can ask again."}
          </dd>
        </div>
      </dl>
      {approve ? (
        <div className="stack verify-field">
          <span className="field-label" id="verify-label">
            How did you confirm identity?
            <span className="subtle">Required</span>
          </span>
          <div className="choice-grid verify-choices" role="group" aria-labelledby="verify-label">
            {VERIFY_METHODS.map((m) => (
              <button
                key={m}
                type="button"
                className="choice"
                aria-pressed={method === m}
                disabled={busy}
                onClick={() => setMethod(m)}
              >
                <span className="choice-title">{VERIFY_LABEL[m]}</span>
                <span className="choice-hint">{VERIFY_HINT[m]}</span>
              </button>
            ))}
          </div>
          {method ? (
            <label className="stack">
              <span className="field-label">
                {other ? "What did you check?" : "Details"}
                <span className="subtle">{other ? "Required" : "Optional"}</span>
              </span>
              <textarea
                rows={3}
                maxLength={450}
                value={note}
                disabled={busy}
                onChange={(e) => setNote(e.target.value)}
                placeholder={VERIFY_PLACEHOLDER[method]}
              />
            </label>
          ) : null}
        </div>
      ) : (
        <label className="stack">
          <span className="field-label">
            Reason
            <span className="subtle">Optional</span>
          </span>
          <textarea
            rows={3}
            maxLength={500}
            value={note}
            disabled={busy}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
      )}
      <ErrorNotice error={error} />
    </ConfirmDialog>
  );
}
